import {select, fork, take, put, call} from 'redux-saga/effects';
import {fetchRequest} from '@computerrock/formation-core';
import {replace, resolveRoute, prepareSearchQueryParams} from '@computerrock/formation-router';
import {Topic, topicTypes} from '@swiss-ski/sski-entity-types';
import routePaths from '../../routePaths';
import * as categoriesActionTypes from '../categoryActionTypes';
import config from '../../config';
import * as applicationActionTypes from '../../application/applicationActionTypes';
import {CATEGORY_ID} from '../../utils/ftemConstants';
import setSportFilter from '../../utils/setSportFilter';
import {snakeCase} from 'change-case';

const getApplicationState = state => state.application;
const getCategoriesState = state => state.categories;

function* waitForCategoryOptions(sportId, ftemStrapiService) {
    while (true) {
        const {categoryOptions} = yield select(getCategoriesState);
        if (categoryOptions?.length > 0) return;

        const filterParamsString = `filters[level][$eq]=detail&filters[sports][$eq]=${sportId}&sort=sortKey`;
        yield fork(
            fetchRequest,
            categoriesActionTypes.FETCH_CATEGORIES_REQUEST,
            ftemStrapiService.getTopics,
            {filterParamsString},
        );

        const responseAction = yield take([
            categoriesActionTypes.FETCH_CATEGORIES_REQUEST_SUCCEEDED,
            categoriesActionTypes.FETCH_CATEGORIES_REQUEST_FAILED,
        ]);

        if (!responseAction.error) {
            const {response} = responseAction.payload;
            yield put({
                type: categoriesActionTypes.STORE_CATEGORIES,
                payload: {
                    topicDTOs: response.topicDTOs,
                    responseLanguage: response.responseLanguage,
                },
            });
        }
    }
}

const getFilterParams = (sportId, isSearch, searchTerm) => {
    if (isSearch && searchTerm) {
        return `filters[sport][$eq]=${sportId}&filters[level][$eq]=detail&sort=subTopic.sortKey&pagination[pageSize]=10000&filters[text][$containsi]=${encodeURIComponent(searchTerm)}`;
    }
    return `filters[level][$eq]=detail&filters[sports][$eq]=${sportId}&sort=sortKey`;
};

function* fetchElements(ftemStrapiService, filterParamsString, categoryIds) {
    yield fork(
        fetchRequest,
        categoriesActionTypes.FETCH_ELEMENTS_REQUEST,
        ftemStrapiService.getElements,
        {filterParamsString},
    );

    const responseAction = yield take([
        categoriesActionTypes.FETCH_ELEMENTS_REQUEST_SUCCEEDED,
        categoriesActionTypes.FETCH_ELEMENTS_REQUEST_FAILED,
    ]);

    if (!responseAction.error) {
        const {response} = responseAction.payload;
        const {elementDTOs} = response;

        yield put({
            type: categoriesActionTypes.STORE_ELEMENTS,
            payload: {elementDTOs, categoryIds},
        });

        const links = elementDTOs.map(element => element.links).flat().filter(Boolean);
        yield put({
            type: applicationActionTypes.STORE_LINKS,
            payload: {links},
        });
    }
}

function* loadCategories({payload}) {
    const {location} = payload;
    const {query, state} = location ?? {};
    if (query?.modal && state?.isModalOpen) return;

    const queryParams = new URLSearchParams(location?.search);
    const sportId = queryParams.get('sportId') || (yield select(getApplicationState)).sportId;
    const searchTerm = queryParams.get('search');
    const locale = queryParams.get('locale');
    const urlCategoryIds = queryParams.getAll(CATEGORY_ID);

    const {serviceManager, sportId: appSportId} = yield select(getApplicationState);
    const ftemStrapiService = serviceManager.loadService('ftemStrapiService');

    if (searchTerm) {
        yield call(waitForCategoryOptions, sportId, ftemStrapiService);

        const {categoryOptions} = yield select(getCategoriesState);

        const availableCategoryIds = categoryOptions
            ?.filter(
                category => category.topicGroup
                    && category.topicGroup !== 'none'
                    && snakeCase(category.id) !== topicTypes.AGE_CATEGORY.toLowerCase(),
            )
            .map(cat => cat.id) || [];

        const filterParamsString = getFilterParams(sportId, true, searchTerm);
        yield call(fetchElements, ftemStrapiService, filterParamsString, availableCategoryIds);
        return;
    }

    if (!urlCategoryIds.length) {
        const queryUrl = prepareSearchQueryParams({
            id: config.DEFAULT_CATEGORIES,
            sport: appSportId,
            ...(locale ? {locale} : {}),
        });
        yield put(replace(resolveRoute(routePaths.CATEGORIES, {}, {search: `${queryUrl}&sportId=${appSportId}`})));
        return;
    }

    let topicDTOs = [];
    const {categoryOptions, categoriesLanguage} = yield select(getCategoriesState);
    if (!categoryOptions.length || (locale && categoriesLanguage !== locale)) {
        const filterParamsString = getFilterParams(sportId, false);
        yield fork(
            fetchRequest,
            categoriesActionTypes.FETCH_CATEGORIES_REQUEST,
            ftemStrapiService.getTopics,
            {filterParamsString},
        );

        const responseAction = yield take([
            categoriesActionTypes.FETCH_CATEGORIES_REQUEST_SUCCEEDED,
            categoriesActionTypes.FETCH_CATEGORIES_REQUEST_FAILED,
        ]);

        if (!responseAction.error) {
            const {response} = responseAction.payload;
            topicDTOs = response.topicDTOs;
            yield put({
                type: categoriesActionTypes.STORE_CATEGORIES,
                payload: {
                    topicDTOs,
                    responseLanguage: response.responseLanguage,
                },
            });
        }
    }

    if (topicDTOs.length || categoryOptions.length) {
        const subTopics = urlCategoryIds
            .map(categoryId => {
                const topic = topicDTOs.length
                    ? new Topic().fromDTO(topicDTOs.find(t => t.itemKey === categoryId))
                    : categoryOptions.find(t => t.id === categoryId);
                return topic?.subTopics;
            })
            .filter(Boolean)
            .flat();

        if (!subTopics.length) return;

        const subTopicFilterParams = subTopics
            .map((subTopic, index) => `filters[subTopic][id]=${subTopic.numID}`)
            .join('&');
        const elementFilterParamsString = `filters[level][$eq]=detail&sort=subTopic.sortKey&pagination[pageSize]=10000&${subTopicFilterParams}`;
        const formatFilterParamsString = elementFilterParamsString + setSportFilter({sportId: sportId || appSportId, lastIndex: subTopics.length - 1});

        yield call(fetchElements, ftemStrapiService, formatFilterParamsString, urlCategoryIds);
    }
}

export default loadCategories;
